import React, { useState, useRef } from 'react';
import { UploadIcon, FileIcon, XIcon, CheckIcon } from 'lucide-react';
import Button from './Button';
interface FileUploaderProps {
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in MB
  onFilesSelected: (files: File[]) => void;
  onError?: (error: string) => void;
}
const FileUploader: React.FC<FileUploaderProps> = ({
  accept = '.csv,.xlsx,.xls',
  multiple = false,
  maxSize = 10,
  // 10MB default
  onFilesSelected,
  onError = () => {}
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    validateAndSetFiles(selectedFiles);
  };
  const validateAndSetFiles = (selectedFiles: File[]) => {
    // Check file type
    const invalidTypeFiles = selectedFiles.filter(file => {
      if (!accept) return false;
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      const acceptTypes = accept.split(',');
      return !acceptTypes.some(type => {
        // Handle wildcards like image/*
        if (type.includes('/*')) {
          const mainType = type.split('/')[0];
          return file.type.startsWith(mainType);
        }
        // Handle specific extensions like .csv
        if (type.startsWith('.')) {
          return fileExtension === type.toLowerCase();
        }
        // Handle specific mime types
        return file.type === type;
      });
    });
    if (invalidTypeFiles.length > 0) {
      onError(`Invalid file type. Accepted types: ${accept.replace(/,/g, ', ')}`);
      return;
    }
    // Check file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSizeBytes);
    if (oversizedFiles.length > 0) {
      onError(`File size exceeds the maximum limit of ${maxSize}MB`);
      return;
    }
    // Set files
    const newFiles = multiple ? [...files, ...selectedFiles] : selectedFiles;
    setFiles(newFiles);
    onFilesSelected(newFiles);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    validateAndSetFiles(droppedFiles);
  };
  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
    onFilesSelected(newFiles);
  };
  const clearFiles = () => {
    setFiles([]);
    onFilesSelected([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' bytes';else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  return <div className="w-full">
      <div className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer ${isDragging ? 'border-navy-500 bg-navy-50' : 'border-ink-300 hover:border-navy-400 hover:bg-ink-50'}`} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={triggerFileInput}>
        <input type="file" ref={fileInputRef} className="hidden" accept={accept} multiple={multiple} onChange={handleFileChange} />
        <UploadIcon className={`h-10 w-10 mb-3 ${isDragging ? 'text-navy-500' : 'text-ink-400'}`} />
        <p className="mb-2 text-sm font-medium text-ink-700">
          Drag and drop your {multiple ? 'files' : 'file'} here
        </p>
        <p className="text-xs text-ink-500 mb-4">
          or click to browse your computer
        </p>
        <p className="text-xs text-ink-500">
          Accepted file types: {accept.replace(/,/g, ', ')}
        </p>
        <p className="text-xs text-ink-500">Maximum file size: {maxSize}MB</p>
      </div>
      {files.length > 0 && <div className="mt-4 space-y-2">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-ink-700">
              Selected {files.length} {files.length === 1 ? 'file' : 'files'}
            </h4>
            <Button variant="secondary" size="sm" onClick={e => {
          e.stopPropagation();
          clearFiles();
        }}>
              Clear All
            </Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {files.map((file, index) => <div key={index} className="flex items-center justify-between p-3 bg-ink-50 border rounded-md">
                <div className="flex items-center">
                  <FileIcon className="h-5 w-5 text-navy-500 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-ink-700 truncate max-w-xs">
                      {file.name}
                    </p>
                    <p className="text-xs text-ink-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button className="text-ink-400 hover:text-danger-fg" onClick={e => {
            e.stopPropagation();
            removeFile(index);
          }}>
                  <XIcon className="h-4 w-4" />
                </button>
              </div>)}
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="primary" leftIcon={<CheckIcon className="h-4 w-4" />} onClick={e => {
          e.stopPropagation();
          // Files are already passed via onFilesSelected
        }}>
              Use Selected {files.length > 1 ? 'Files' : 'File'}
            </Button>
          </div>
        </div>}
    </div>;
};
export default FileUploader;